import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import clsx from 'clsx';
import { VacationRequestSummary } from '../VacationRequestSummary';
import { VacationEventHistory } from '../VacationEventHistory';
import { SegmentCorrectionControl } from '../SegmentCorrectionControl';
import { isoToBrDate } from '../../utils/vacationDates';
import { formatPeriodLabel } from '../../utils/vacationHistoryPeriods';
import { resolveSegmentDecision } from '../../utils/vacationSegments';
import type { CorrectionDraft } from './vacationRequestDetailsModal.types';
import {
    getRequestStatusMetadata,
    getReceiptPendingLabel,
} from '../../vacations.definitions';
import { canDeleteVacationRequest, canConfirmVacationRequest } from '../../utils/vacationRequestStatus';
import { useDraftCountdown } from '../../hooks/useDraftCountdown';
import {
    DeleteRequestAction,
    ConfirmRequestAction,
    RequestClients,
    DownloadReceiptAction,
} from './components';
import { CLOSE_LABEL, MODAL_TITLE, CONFIRM_WARNING, DELETE_WARNING, buildDraftCountdownLabel } from './vacationRequestDetailsModal.definitions';
import type { VacationRequestDetailsModalProps } from './vacationRequestDetailsModal.types';

export function VacationRequestDetailsModal({
    request,
    onClose,
    receipts,
    currentUserEmail,
    onDeleteRequest,
    isDeleting = false,
    onConfirmRequest,
    isConfirmingRequest = false,
    onResendClientEmail,
    resendingEmail,
    onSubmitCorrection,
    isSubmittingCorrection = false,
    collaboratorName,
    periodAllocations,
}: VacationRequestDetailsModalProps) {
    const [openAction, setOpenAction] = useState<'confirm' | 'delete' | null>(null);
    const [correctionDrafts, setCorrectionDrafts] = useState<Record<number, CorrectionDraft>>({});
    const { remainingLabel, isExpired } = useDraftCountdown(request);

    if (!request) return null;

    const statusMeta = getRequestStatusMetadata(request);
    const receiptPendingLabel = getReceiptPendingLabel(request);
    const canDelete = Boolean(onDeleteRequest) && !isExpired && canDeleteVacationRequest(request, currentUserEmail);
    const canConfirm = Boolean(onConfirmRequest) && canConfirmVacationRequest(request, currentUserEmail);

    const isPendingCorrection = request.status === 'PendingCorrection';
    const refusedOrders = isPendingCorrection
        ? request.segments
            .filter((segment) => resolveSegmentDecision(request, segment.order)?.status === 'Rejected')
            .map((segment) => segment.order)
        : [];
    const correctionReady = refusedOrders.length > 0 && refusedOrders.every((order) => correctionDrafts[order]);

    const updateDraft = (order: number, draft: CorrectionDraft | null) => {
        setCorrectionDrafts((prev) => {
            const next = { ...prev };
            if (draft) next[order] = draft;
            else delete next[order];
            return next;
        });
    };

    const submitCorrections = async () => {
        if (!onSubmitCorrection || !correctionReady) return;
        try {
            await onSubmitCorrection(
                request.id,
                refusedOrders.map((order) => ({ order, ...correctionDrafts[order] }))
            );
            setCorrectionDrafts({});
        } catch {
            return;
        }
    };

    return (
        <Dialog open onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
                    <div className="border-b border-gray-200 px-7 py-5">
                        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                            <div>
                                <Dialog.Title className="text-lg font-semibold text-raro-gray-900 font-montserrat">
                                    {MODAL_TITLE}
                                </Dialog.Title>
                                {collaboratorName && (
                                    <p className="text-sm text-gray-600 mt-1">{collaboratorName}</p>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                <span
                                    className={clsx(
                                        'rounded-md px-2.5 py-1 text-sm font-montserrat',
                                        statusMeta.className
                                    )}
                                >
                                    {statusMeta.label}
                                </span>
                                {receiptPendingLabel && (
                                    <span className="rounded-md bg-amber-100 px-2.5 py-1 text-sm text-amber-800 font-montserrat">
                                        {receiptPendingLabel}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-7 py-5">
                        <VacationRequestSummary
                            request={request}
                            concessiveDeadline={request.concessiveDeadline ?? null}
                            actionPlacement="plain"
                            renderPeriodAction={
                                isPendingCorrection && onSubmitCorrection
                                    ? (segment, decision) => {
                                          if (decision?.status !== 'Rejected') return null;
                                          return (
                                              <SegmentCorrectionControl
                                                  segment={segment}
                                                  request={request}
                                                  holidays={[]}
                                                  isSubmitting={isSubmittingCorrection}
                                                  onDraftChange={(draft) => updateDraft(segment.order, draft)}
                                              />
                                          );
                                      }
                                    : undefined
                            }
                        />

                        {refusedOrders.length > 0 && onSubmitCorrection && (
                            <div className="flex flex-col items-end gap-2">
                                {!correctionReady && (
                                    <p className="text-xs text-raro-gray-600 font-montserrat">
                                        Escolha a nova data de cada período reprovado para enviar a correção.
                                    </p>
                                )}
                                <button
                                    type="button"
                                    onClick={submitCorrections}
                                    disabled={!correctionReady || isSubmittingCorrection}
                                    className="whitespace-nowrap rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 font-montserrat"
                                >
                                    {isSubmittingCorrection ? 'Enviando correção...' : 'Enviar correção'}
                                </button>
                            </div>
                        )}

                        {periodAllocations && periodAllocations.length > 0 && (
                            <div className="rounded-lg bg-gray-50 p-4">
                                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">
                                    Período aquisitivo
                                </p>
                                <div className="space-y-2">
                                    {periodAllocations.map((alloc, idx) => (
                                        <div key={idx} className="text-sm text-gray-900">
                                            {isoToBrDate(alloc.startDate)} a {isoToBrDate(alloc.endDate)} · gozar até{' '}
                                            {isoToBrDate(alloc.concessiveDeadline)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <RequestClients
                            clientEmails={request.clientEmails}
                            clientApproval={request.clientApproval}
                            request={request}
                            onResendClientEmail={onResendClientEmail}
                            resendingEmail={resendingEmail}
                        />

                        <VacationEventHistory requestId={request.id} request={request} />
                    </div>

                    <div className="space-y-2 border-t border-gray-200 bg-gray-50 px-7 py-4">
                        {openAction && (
                            <p className={clsx('text-xs font-montserrat', openAction === 'delete' ? 'text-red-700' : 'text-primary-700')}>
                                {openAction === 'delete' ? DELETE_WARNING : CONFIRM_WARNING}
                            </p>
                        )}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                                {canConfirm && onConfirmRequest && openAction !== 'delete' && (
                                    <ConfirmRequestAction
                                        onConfirm={() => onConfirmRequest(request.id)}
                                        isConfirming={isConfirmingRequest}
                                        isOpen={openAction === 'confirm'}
                                        onOpenChange={(open) => setOpenAction(open ? 'confirm' : null)}
                                        showWarning={false}
                                    />
                                )}
                                {canDelete && onDeleteRequest && openAction !== 'confirm' && (
                                    <DeleteRequestAction
                                        onConfirm={() => onDeleteRequest(request.id)}
                                        isDeleting={isDeleting}
                                        isOpen={openAction === 'delete'}
                                        onOpenChange={(open) => setOpenAction(open ? 'delete' : null)}
                                        showWarning={false}
                                    />
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {receipts && receipts.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        {receipts.map((receipt) => (
                                            <DownloadReceiptAction
                                                key={receipt.id}
                                                receipt={receipt}
                                                label={
                                                    receipts.length > 1 && receipt.segmentOrder !== null
                                                        ? `Recibo ${receipt.segmentOrder}º`
                                                        : undefined
                                                }
                                            />
                                        ))}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="whitespace-nowrap rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-raro-gray-700 transition-colors hover:bg-gray-50 font-montserrat"
                                >
                                    {CLOSE_LABEL}
                                </button>
                            </div>
                        </div>
                        {canDelete && (
                            <p className="text-xs text-raro-gray-600 font-montserrat">
                                {buildDraftCountdownLabel(remainingLabel)}
                            </p>
                        )}
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}

export default VacationRequestDetailsModal;
